import React, { useState } from 'react';
import { useCategories } from '../hooks/useCategories';
import { Category } from '../services/categoryService';

interface CategoryPickerProps {
  selectedCategory?: string;
  onCategorySelect?: (categoryId: string) => void;
  value?: string;
  onChange?: (categoryId: string) => void;
  placeholder?: string;
  className?: string;
}

export function CategoryPicker({ 
  selectedCategory, 
  onCategorySelect,
  value,
  onChange,
  placeholder = "Select Category",
  className = ""
}: CategoryPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { categories, loading } = useCategories({ activeOnly: true });

  const currentValue = value || selectedCategory;
  const selectedCategoryData = categories.find(cat => cat.id === currentValue);

  const handleCategorySelect = (category: Category) => {
    if (category.id) {
      if (onChange) {
        onChange(category.id);
      } else if (onCategorySelect) {
        onCategorySelect(category.id);
      }
      setIsOpen(false);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const renderSelectedCategory = () => {
    if (selectedCategoryData) {
      return (
        <div className="flex items-center gap-2">
          {selectedCategoryData.icon && (
            <span className="text-base">{selectedCategoryData.icon}</span>
          )}
          <span className="text-gray-900 font-medium">{selectedCategoryData.name}</span>
        </div>
      );
    }
    
    return <span className="text-gray-500">{placeholder}</span>;
  };

  return (
    <div className={`relative w-full max-w-md ${className}`}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        className="input-field flex items-center justify-between w-full text-left focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      >
        {renderSelectedCategory()}
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                <p className="text-sm text-gray-600 mt-2">Loading categories...</p>
              </div>
            ) : categories.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No categories available
              </div>
            ) : (
              <div className="py-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleCategorySelect(category)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors ${
                      currentValue === category.id ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Category Icon */}
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg"
                        style={{ backgroundColor: category.color || '#3b82f6' }}
                      >
                        <span>{category.icon}</span>
                      </div>
                      
                      {/* Category Info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">
                          {category.name}
                        </div>
                        {category.description && (
                          <div className="text-xs text-gray-500 truncate">
                            {category.description}
                          </div>
                        )}
                      </div>

                      {/* Selected Indicator */}
                      {currentValue === category.id && (
                        <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}